import { MediaSelect } from "@/(admin)/modules/media/pages/SelectMediaUIBuilder"
import { CommonStylesProps } from "@/(admin)/modules/ui-builder/config/lib/commonCSSProps"
import { CategoryConfig } from "@/(admin)/modules/ui-builder/config/themes/tamda-media/TamdaMediaNewsLayout/types/config"
import { useCategory } from "@/hook/useCategory"
import { CategoryData } from "@/lib/config/types"
import { ComponentConfig } from "@measured/puck"
import { Button, Select, Typography } from "antd"
import { useCallback, useEffect, useMemo, useState } from "react"
import SelectBanner from "../../../components/SelectBanner"
import { RenderConfig } from "./RenderConfig"

export interface SectionBannerSidebarProps {
  className: string
  styles?: CommonStylesProps["styles"]
  responsiveType?: CommonStylesProps["responsiveType"]
  animation?: CommonStylesProps["animation"]
  categoryIds: CategoryConfig[]
  bannerIds: Array<{ bannerId: string; showMobile?: boolean }>
  banner: string
}

function CategorySelect(props: any) {
  const { fetchCategories, loading: hookLoading } = useCategory()
  const [allCategories, setAllCategories] = useState<CategoryData[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)

  const loadCategories = useCallback(
    async (page: number) => {
      try {
        setLoading(true)
        const response: any = await fetchCategories({ page: String(page), pageSize: "5" }, "preview")

        if (response?.data) {
          setAllCategories(prev => {
            const existingIds = new Set(prev.map(cat => cat.id))
            const newCategories = response.data.filter((cat: CategoryData) => !existingIds.has(cat.id))
            return [...prev, ...newCategories]
          })

          const total = response?.meta?.pagination?.total ?? 0
          const pageSize = response?.meta?.pagination?.pageSize ?? 5
          setHasMore(page * pageSize < total)
        }
      } catch (error) {
        console.error("Failed to load categories:", error)
      } finally {
        setLoading(false)
      }
    },
    [fetchCategories],
  )

  useEffect(() => {
    loadCategories(1)
  }, [])

  const handleLoadMore = () => {
    const nextPage = currentPage + 1
    setCurrentPage(nextPage)
    loadCategories(nextPage)
  }

  const options = useMemo(
    () =>
      allCategories.map((category: CategoryData) => ({
        value: category.id,
        label: category.attributes.name,
      })),
    [allCategories],
  )

  const handleChange = (value: number) => {
    const selectedCategory = allCategories.find(cat => cat.id === value)
    if (selectedCategory && props.onChange) {
      props.onChange({
        categoryId: value,
        categoryName: selectedCategory.attributes.name,
      })
    }
  }

  const { Label, onChange, value, ...selectProps } = props
  const labelText = typeof Label === "function" ? "Select Category" : Label

  return (
    <div>
      <Typography.Title level={5}>Select Category</Typography.Title>
      <Select
        {...selectProps}
        value={value?.categoryId}
        onChange={handleChange}
        options={options}
        className="w-full"
        loading={loading || hookLoading}
        placeholder={labelText || "Select Category"}
        popupRender={menu => (
          <>
            {menu}
            {hasMore && (
              <Button
                onClick={handleLoadMore}
                loading={loading}
                style={{ width: "100%", marginTop: 8 }}
                type="dashed"
                size="small"
              >
                Xem thêm
              </Button>
            )}
          </>
        )}
      />
    </div>
  )
}

export const SectionBannerSidebar: ComponentConfig<SectionBannerSidebarProps> = {
  label: "Tamda Media | Section Banner Sidebar",
  fields: {
    categoryIds: {
      label: "Categories",
      type: "array",
      arrayFields: {
        category: {
          label: "Category",
          type: "custom",
          render: (props: any) => <CategorySelect {...props} />,
        },
      },
      getItemSummary: (item: any) => item.category?.categoryName || `Category ${item.category?.categoryId || ""}`,
      defaultItemProps: {
        category: {
          categoryId: 0,
          categoryName: "",
        },
      },
    },
    bannerIds: {
      label: "Banners",
      type: "array",
      arrayFields: {
        bannerId: {
          label: "Banner",
          type: "custom",
          render: ({ value, onChange }: any) => (
            <SelectBanner placeholder="Chọn banner" value={value} onChange={val => onChange(val ?? "")} />
          ),
        },
        showMobile: {
          label: "Hiển thị trên Mobile",
          type: "radio",
          options: [
            { value: true, label: "Yes" },
            { value: false, label: "No" },
          ],
        },
      },
      getItemSummary: (item: any) => `Banner ${item.bannerId || ""}`,
      defaultItemProps: {
        bannerId: "",
        showMobile: true,
      },
    },
    banner: {
      label: "Banner",
      type: "custom",
      render: (props: any) => <MediaSelect {...props} mediaType="image" />,
    },
    className: {
      label: "Class Name",
      type: "text",
    },
  },
  defaultProps: {
    className: "",
    bannerIds: [],
    categoryIds: [],
    banner: "",
  },
  ...RenderConfig,
}
